import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FieldSelect } from "@/components/ui/field-select";
import { LifeBuoy, Loader2, Paperclip, Send, Search, ArrowLeft, Building2, AlertTriangle, Clock, Inbox } from "lucide-react";

const CATS = { technical: "Problème technique", billing: "Abonnement & facturation", help: "Besoin d'aide", improvement: "Demande d'amélioration", other: "Autre" };
const STATUS = { nouveau: ["Nouveau", "bg-blue-50 text-blue-700"], en_cours: ["En cours", "bg-amber-50 text-amber-700"], en_attente_client: ["En attente client", "bg-purple-50 text-purple-700"], resolu: ["Résolu", "bg-emerald-50 text-emerald-700"] };
const PRIO = { normale: ["Normale", "bg-slate-100 text-slate-600"], haute: ["Haute", "bg-orange-50 text-orange-700"], urgente: ["Urgente", "bg-rose-50 text-rose-700"] };
const CAT_OPTS = Object.entries(CATS).map(([value, label]) => ({ value, label }));
const STATUS_OPTS = Object.entries(STATUS).map(([value, [label]]) => ({ value, label }));
const PRIO_OPTS = Object.entries(PRIO).map(([value, [label]]) => ({ value, label }));
const PLAN_OPTS = [{ value: "pro", label: "Pro" }, { value: "business", label: "Business" }, { value: "premium", label: "Premium" }, { value: "enterprise", label: "Enterprise" }];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}

export default function AdminSupport() {
  const { ticketId } = useParams();
  if (ticketId) return <TicketDetail ticketId={ticketId} />;
  return <TicketList />;
}

function StatCard({ icon: Icon, label, value, tone, testId }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid={testId}>
      <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${tone}`}><Icon className="h-4 w-4" /></div>
      <div><p className="text-xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
    </div>
  );
}

function TicketList() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [status, setStatus] = useState("all"); const [priority, setPriority] = useState(sp.get("priority") || "all");
  const [plan, setPlan] = useState("all"); const [category, setCategory] = useState("all");
  const [orgId, setOrgId] = useState("all");
  const [q, setQ] = useState(sp.get("q") || ""); const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data: orgsData } = useQuery({
    queryKey: ["admin-support-orgs"],
    queryFn: async () => (await api.get("/api/admin/orgs", { params: { page: 1, page_size: 100 } })).data,
    retry: false,
  });
  const orgOpts = [{ value: "all", label: "Toutes organisations" },
    ...(orgsData?.items || []).map((o) => ({ value: o.id, label: o.name }))];

  const { data, isLoading } = useQuery({
    queryKey: ["admin-support", status, priority, plan, category, orgId, q, page],
    queryFn: async () => (await api.get("/api/admin/support/tickets", { params: {
      status: status === "all" ? undefined : status,
      priority: priority === "all" ? undefined : priority,
      plan: plan === "all" ? undefined : plan,
      category: category === "all" ? undefined : category,
      organization_id: orgId === "all" ? undefined : orgId,
      q: q || undefined, page, page_size: pageSize } })).data,
    retry: false, keepPreviousData: true,
  });
  const tickets = data?.items || []; const total = data?.total || 0; const stats = data?.stats || {};
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-support-page">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><LifeBuoy className="h-5 w-5" /></div>
        <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">Support</h1>
          <p className="text-sm text-slate-500">Tous les tickets clients ActiPilote</p></div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard testId="admin-support-stat-open" icon={Inbox} label="Tickets ouverts" value={stats.open ?? "—"} tone="bg-blue-50 text-blue-600" />
        <StatCard testId="admin-support-stat-urgent" icon={AlertTriangle} label="Urgents" value={stats.urgent ?? "—"} tone="bg-rose-50 text-rose-600" />
        <StatCard testId="admin-support-stat-waiting" icon={Clock} label="En attente client" value={stats.waiting_client ?? "—"} tone="bg-purple-50 text-purple-600" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher (numéro, objet, organisation)" data-testid="admin-support-search"
            className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
        </div>
        <FieldSelect testId="admin-support-filter-org" className="w-52" value={orgId} onChange={(v) => { setOrgId(v); setPage(1); }} options={orgOpts} />
        <FieldSelect testId="admin-support-filter-status" className="w-40" value={status} onChange={(v) => { setStatus(v); setPage(1); }}
          options={[{ value: "all", label: "Tous les statuts" }, ...STATUS_OPTS]} />
        <FieldSelect testId="admin-support-filter-priority" className="w-36" value={priority} onChange={(v) => { setPriority(v); setPage(1); }}
          options={[{ value: "all", label: "Toutes priorités" }, ...PRIO_OPTS]} />
        <FieldSelect testId="admin-support-filter-plan" className="w-36" value={plan} onChange={(v) => { setPlan(v); setPage(1); }}
          options={[{ value: "all", label: "Tous les plans" }, ...PLAN_OPTS]} />
        <FieldSelect testId="admin-support-filter-category" className="w-48" value={category} onChange={(v) => { setCategory(v); setPage(1); }}
          options={[{ value: "all", label: "Toutes catégories" }, ...CAT_OPTS]} />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : tickets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="admin-support-empty">
          <LifeBuoy className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucun ticket ne correspond à ces filtres.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-support-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>{["Ticket", "Organisation", "Plan", "Catégorie", "Priorité", "Statut", "Maj"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tickets.map((t) => {
                const [sl, sc] = STATUS[t.status] || ["", ""]; const [pl, pc] = PRIO[t.priority] || ["", ""];
                return (
                  <tr key={t.id} onClick={() => navigate(`/admin/support/${t.id}`)} data-testid={`admin-ticket-row-${t.id}`} className="cursor-pointer hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs font-semibold text-slate-400">{t.ticket_number}</p>
                      <p className="max-w-[220px] truncate font-medium text-slate-800">{t.subject}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{t.organization_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 capitalize">{t.organization_plan || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{CATS[t.category] || t.category}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${pc}`}>{pl}</span></td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${sc}`}>{sl}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-400">{timeAgo(t.last_message_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span data-testid="admin-support-total">{total} ticket{total > 1 ? "s" : ""}</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-support-prev" className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40">Précédent</button>
            <span>Page {page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="admin-support-next" className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40">Suivant</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TicketDetail({ ticketId }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [reply, setReply] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-support-ticket", ticketId],
    queryFn: async () => (await api.get(`/api/admin/support/tickets/${ticketId}`)).data,
    retry: false,
  });
  const t = data?.ticket; const org = t?.organization || {}; const msgs = data?.messages || [];

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-support-ticket", ticketId] }); qc.invalidateQueries({ queryKey: ["admin-support"] }); };

  const replyMut = useMutation({
    mutationFn: async () => (await api.post(`/api/admin/support/tickets/${ticketId}/reply`, { message: reply })).data,
    onSuccess: () => { setReply(""); toast.success("Réponse envoyée au client"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const statusMut = useMutation({
    mutationFn: async (status) => (await api.post(`/api/admin/support/tickets/${ticketId}/status`, { status })).data,
    onSuccess: () => { toast.success("Statut mis à jour"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const prioMut = useMutation({
    mutationFn: async (priority) => (await api.post(`/api/admin/support/tickets/${ticketId}/priority`, { priority })).data,
    onSuccess: () => { toast.success("Priorité mise à jour"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <div data-testid="admin-ticket-detail">
      <button onClick={() => navigate("/admin/support")} data-testid="admin-ticket-back" className="mb-4 flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Retour aux tickets</button>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : !t ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500">Ticket introuvable.</div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="mb-1 flex items-center gap-2">
                <span className="font-mono text-xs font-semibold text-slate-400">{t.ticket_number}</span>
                <span className="text-xs text-slate-400">· {CATS[t.category] || t.category}</span>
              </div>
              <h1 className="text-lg font-bold text-slate-900" data-testid="admin-ticket-subject">{t.subject}</h1>

              <div className="mt-4 max-h-[52vh] space-y-3 overflow-y-auto pr-1" data-testid="admin-ticket-conversation">
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.author_type === "support" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.author_type === "support" ? "bg-primary text-white" : "bg-slate-100 text-slate-800"}`}>
                      <p className="mb-0.5 text-[11px] font-semibold opacity-70">{m.author_name}</p>
                      <p className="whitespace-pre-wrap">{m.message}</p>
                      {m.attachment && <a href={`${api.defaults.baseURL}/api/admin/support/attachments/${m.attachment.file_id}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-ticket-attachment-${m.id}`} className="mt-1 flex items-center gap-1 text-xs underline"><Paperclip className="h-3 w-3" />{m.attachment.filename}</a>}
                      <p className="mt-1 text-[10px] opacity-60">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Répondre au client…" data-testid="admin-ticket-reply-input"
                  className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                <div className="mt-2 flex justify-end">
                  <button onClick={() => replyMut.mutate()} disabled={!reply.trim() || replyMut.isPending} data-testid="admin-ticket-reply-submit"
                    className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                    {replyMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-ticket-org">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900"><Building2 className="h-4 w-4 text-slate-400" /> Organisation</div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Nom</dt><dd className="text-right font-medium text-slate-800">{org.name || "—"}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Propriétaire</dt><dd className="text-right text-slate-800">{org.owner_name || "—"}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Email</dt><dd className="text-right text-slate-800 break-all">{org.owner_email || "—"}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Plan</dt><dd className="text-right font-medium capitalize text-slate-800">{org.plan || "—"}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Abonnement</dt><dd className="text-right text-slate-800">{org.subscription_status || "—"}{org.restricted && <span className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-600">Restreint</span>}</dd></div>
              </dl>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="mb-2 text-sm font-semibold text-slate-900">Statut</p>
              <FieldSelect testId="admin-ticket-status-select" className="w-full" value={t.status} onChange={(v) => statusMut.mutate(v)} options={STATUS_OPTS} />
              <p className="mb-2 mt-4 text-sm font-semibold text-slate-900">Priorité</p>
              <FieldSelect testId="admin-ticket-priority-select" className="w-full" value={t.priority} onChange={(v) => prioMut.mutate(v)} options={PRIO_OPTS} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
